import type { WebhookFilters } from '@monorepo/shared/schemas/webhooks'
import { Bot, ChevronDown, ChevronRight, Loader2 } from 'lucide-react'
import { useState } from 'react'
import { useAgentActions } from '@/features/agents/hooks/useAgentActions'
import { useAgentsList } from '@/features/agents/hooks/useAgents'
import { Badge } from '@/shared/components/ui/badge'
import { Button } from '@/shared/components/ui/button'
import { Checkbox } from '@/shared/components/ui/checkbox'
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from '@/shared/components/ui/collapsible'
import { Label } from '@/shared/components/ui/label'

interface AgentActionFilterProps {
  filters: WebhookFilters
  onFiltersChange: (filters: WebhookFilters) => void
}

function AgentActionsSection({
  agentId,
  selectedActionIds,
  onToggleAction,
}: {
  agentId: string
  selectedActionIds: string[]
  onToggleAction: (actionId: string, checked: boolean) => void
}) {
  const { data, isLoading } = useAgentActions(agentId)
  const actions = data?.data || []

  if (isLoading) {
    return (
      <div className='flex items-center gap-2 py-2 text-sm text-muted-foreground'>
        <Loader2 className='w-3 h-3 animate-spin' />
        Loading actions...
      </div>
    )
  }

  if (actions.length === 0) {
    return (
      <p className='text-sm text-muted-foreground py-1'>No actions defined</p>
    )
  }

  return (
    <div className='space-y-2'>
      <p className='text-xs text-muted-foreground'>
        Select specific actions, or leave unchecked for all actions
      </p>
      {actions.map(action => (
        <div key={action.id} className='flex items-center space-x-2 py-1'>
          <Checkbox
            id={`action-${action.id}`}
            checked={selectedActionIds.includes(action.id)}
            onCheckedChange={checked =>
              onToggleAction(action.id, checked as boolean)
            }
          />
          <Label
            htmlFor={`action-${action.id}`}
            className='cursor-pointer flex-1 flex items-center gap-2'
          >
            {action.action_name}
            <Badge variant='outline' className='text-xs'>
              {action.action_type}
            </Badge>
          </Label>
        </div>
      ))}
    </div>
  )
}

export function AgentActionFilter({
  filters,
  onFiltersChange,
}: AgentActionFilterProps) {
  const { agents, isLoading } = useAgentsList()
  const [expandedAgents, setExpandedAgents] = useState<string[]>([])

  const selectedAgentIds = (filters.agents || []).map(a => a.agent_id)

  const toggleAgentExpanded = (agentId: string) => {
    setExpandedAgents(prev =>
      prev.includes(agentId)
        ? prev.filter(id => id !== agentId)
        : [...prev, agentId]
    )
  }

  const toggleAgent = (agentId: string, checked: boolean) => {
    const current = filters.agents || []
    if (checked) {
      onFiltersChange({
        ...filters,
        agents: [...current, { agent_id: agentId }],
      })
    } else {
      onFiltersChange({
        ...filters,
        agents: current.filter(a => a.agent_id !== agentId),
      })
      setExpandedAgents(prev => prev.filter(id => id !== agentId))
    }
  }

  const toggleAction = (
    agentId: string,
    actionId: string,
    checked: boolean
  ) => {
    const current = filters.agents || []
    const agentFilter = current.find(a => a.agent_id === agentId)
    if (!agentFilter) return

    const currentActionIds = agentFilter.action_ids || []
    const newActionIds = checked
      ? [...currentActionIds, actionId]
      : currentActionIds.filter(id => id !== actionId)

    onFiltersChange({
      ...filters,
      agents: current.map(a =>
        a.agent_id === agentId
          ? {
              ...a,
              action_ids: newActionIds.length > 0 ? newActionIds : undefined,
            }
          : a
      ),
    })
  }

  const agentCount = selectedAgentIds.length

  return (
    <div className='space-y-4'>
      <div className='flex items-center justify-between'>
        <div>
          <h3 className='font-medium'>Filter by Agents (Optional)</h3>
          <p className='text-sm text-muted-foreground'>
            Scope this webhook to specific agents and actions
          </p>
        </div>
        <div className='text-sm text-muted-foreground'>
          {agentCount === 0
            ? 'All agents'
            : `${agentCount} ${agentCount === 1 ? 'agent' : 'agents'} selected`}
        </div>
      </div>

      <div className='border rounded-lg'>
        {isLoading ? (
          <div className='flex items-center justify-center gap-2 py-6 text-sm text-muted-foreground'>
            <Loader2 className='w-4 h-4 animate-spin' />
            Loading agents...
          </div>
        ) : agents.length === 0 ? (
          <div className='text-center py-6 text-sm text-muted-foreground'>
            No agents found
          </div>
        ) : (
          agents.map((agent, index) => {
            const isSelected = selectedAgentIds.includes(agent.id)
            const isExpanded = expandedAgents.includes(agent.id)
            const agentFilter = (filters.agents || []).find(
              a => a.agent_id === agent.id
            )
            const actionCount = agentFilter?.action_ids?.length || 0

            return (
              <div key={agent.id} className={`${index > 0 ? 'border-t' : ''}`}>
                <Collapsible
                  open={isExpanded}
                  onOpenChange={() => toggleAgentExpanded(agent.id)}
                >
                  <div className='flex items-center space-x-2 p-3 hover:bg-muted/50'>
                    <CollapsibleTrigger asChild>
                      <Button
                        variant='ghost'
                        size='sm'
                        className='h-6 w-6 p-0'
                        disabled={!isSelected}
                      >
                        {isExpanded && isSelected ? (
                          <ChevronDown className='w-4 h-4' />
                        ) : (
                          <ChevronRight className='w-4 h-4' />
                        )}
                      </Button>
                    </CollapsibleTrigger>

                    <Checkbox
                      id={`agent-filter-${agent.id}`}
                      checked={isSelected}
                      onCheckedChange={checked =>
                        toggleAgent(agent.id, checked as boolean)
                      }
                    />

                    <div className='flex items-center gap-2 flex-1'>
                      <Bot className='w-4 h-4' />
                      <Label
                        htmlFor={`agent-filter-${agent.id}`}
                        className='font-medium cursor-pointer flex-1'
                      >
                        {agent.name}
                      </Label>
                      {isSelected && actionCount > 0 && (
                        <span className='text-xs text-muted-foreground'>
                          {actionCount}{' '}
                          {actionCount === 1 ? 'action' : 'actions'}
                        </span>
                      )}
                      {isSelected && actionCount === 0 && (
                        <span className='text-xs text-muted-foreground'>
                          All actions
                        </span>
                      )}
                    </div>
                  </div>

                  {isSelected && (
                    <CollapsibleContent>
                      <div className='pl-12 pr-3 pb-3'>
                        <AgentActionsSection
                          agentId={agent.id}
                          selectedActionIds={agentFilter?.action_ids || []}
                          onToggleAction={(actionId, checked) =>
                            toggleAction(agent.id, actionId, checked)
                          }
                        />
                      </div>
                    </CollapsibleContent>
                  )}
                </Collapsible>
              </div>
            )
          })
        )}
      </div>
    </div>
  )
}
